import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, IconButton, 
  Typography, CloseIcon, Select, MenuItem
} from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useEffect, useState } from 'react';
import { api } from 'src/utils';
import { useFeatureTravellerStore } from 'src/stores';
import { MapFeatureCategories, MapFeatureCountries } from 'src/data';

export default function EditFeatureDetailsModal({ onClose }) {
  const isMobile = useIsMobile();

  const [feature, setFeature] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const selectedFeature = useFeatureTravellerStore((state) => state.selectedFeature);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    country: '',
    googleMapsUrl: ''
  });

  const [loading, setLoading] = useState(false);

  // Fetch map feature data
  useEffect(() => {
    if (selectedFeature === null || !selectedFeature) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/map-features/${selectedFeature.id}`);
        const data = res.data;
        setFeature(data);
        setFormData({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          country: data.country || '',
          googleMapsUrl: data.googleMapsUrl || ''
        });
      } catch (err) {
        console.error('Failed to load feature:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedFeature]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // 1. Update feature metadata
      await api.put(`/map-features/${selectedFeature.id}`, {
        ...feature,
        ...formData,
        updatedAt: new Date().toISOString()
      });

      // 2. If images selected, upload it
      if (imageFiles.length > 0) {
        for (const file of imageFiles) {
          const formDataImage = new FormData();
          formDataImage.append('image', file);

          await api.post(`/map-features/${selectedFeature.id}/upload-image`, formDataImage, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      onClose();
    } catch (err) {
      console.error(err);
      alert('Failed to save. See console.');
    } finally {
      setLoading(false);
    }
  };

  if (!feature) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2, overflow: 'hidden', paddingTop: isMobile ? 'var(--safe-area-top)' : 0 },
      }}
    >

      <IconButton 
        onClick={onClose} 
        sx={{ 
          position: 'absolute', 
          top: isMobile ? 'calc(12px + var(--safe-area-top))' : 8,
          right: 8, 
          backgroundColor: 'rgba(255,255,255,0.8)' 
        }}>
        <CloseIcon />
      </IconButton>

      <DialogTitle>Edit Pin Details</DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        <TextField
          name="title"
          label="Title"
          value={formData.title}
          onChange={handleChange}
          fullWidth
        />

        <TextField
          name="description"
          label="Description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={3}
          fullWidth
        />

        <Select
          name="category"
          value={formData.category}
          onChange={handleChange}
          fullWidth
          displayEmpty
        >
          <MenuItem value="" disabled>Select Category</MenuItem>
          {MapFeatureCategories.map((cat) => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </Select>

        <Box mt={1} mb={2}>
          <Typography variant="caption">{feature.images.length} Image(s) Already in the Database</Typography>
          <input
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/heic,image/heif"
            multiple
            onChange={(e) => setImageFiles(Array.from(e.target.files))}
          />
        </Box>

        <Select
          name="country"
          value={formData.country}
          onChange={handleChange}
          fullWidth
          displayEmpty
        >
          <MenuItem value="" disabled>Select Country</MenuItem>
          {MapFeatureCountries.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>

        <TextField
          name="googleMapsUrl"
          label="Google Maps URL"
          value={formData.googleMapsUrl}
          onChange={handleChange}
          fullWidth
        />

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
